import { z } from 'zod'

/**
 * File リソースの Zod スキーマ。
 *
 * 関連: docs/system-requirements.md §4.3 / packages/db/prisma/schema.prisma の File model
 */

export const FileSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  originalName: z.string(),
  sizeBytes: z.string(), // BigInt は JSON 化で文字列化する
  durationSec: z.number().int().nullable(),
  mimeType: z.string(),
  storageKey: z.string(),
  thumbnailKey: z.string().nullable(),
  /**
   * 表示可能なサムネイル URL。
   * - 動画: streamStatus='READY' の時のみ Cloudflare Stream の公開サムネ URL
   * - それ以外（画像/PDF など）: null（MVP ではサーバ側生成しない）
   * 公開 URL のため CDN にキャッシュされ、再取得でも追加コストはほぼゼロ。
   */
  thumbnailUrl: z.string().url().nullable(),
  streamUid: z.string().nullable(),
  streamStatus: z.enum(['PENDING', 'QUEUED', 'READY', 'ERROR']).nullable(),
  archivedAt: z.string().datetime().nullable(),
  uploadedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type File = z.infer<typeof FileSchema>

// ----- Path / Query parameters -----

export const ProjectFilesParamsSchema = z.object({
  id: z.string().uuid(), // project id
})

export const ProjectFileParamsSchema = z.object({
  id: z.string().uuid(), // project id
  fileId: z.string().uuid(),
})

export const FileIdParamsSchema = z.object({
  id: z.string().uuid(), // file id
})

export const ListFilesQuerySchema = z.object({
  archived: z.enum(['true', 'false']).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(50),
})
export type ListFilesQuery = z.infer<typeof ListFilesQuerySchema>

// ----- Upload URL request -----

const MAX_FILE_NAME_LEN = 500
const MAX_MIME_LEN = 100

export const RequestUploadUrlInputSchema = z.object({
  originalName: z.string().min(1).max(MAX_FILE_NAME_LEN),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(50 * 1024 * 1024 * 1024 * 1024, 'sizeBytes が大きすぎます'), // 50TB ガード
  mimeType: z.string().min(1).max(MAX_MIME_LEN),
})
export type RequestUploadUrlInput = z.infer<typeof RequestUploadUrlInputSchema>

const SinglePresignedUrlSchema = z.object({
  kind: z.literal('single'),
  uploadUrl: z.string().url(),
})

const MultipartPresignedUrlSchema = z.object({
  kind: z.literal('multipart'),
  uploadId: z.string(),
  partSizeBytes: z.number().int().positive(),
  parts: z.array(
    z.object({
      partNumber: z.number().int().positive(),
      uploadUrl: z.string().url(),
    }),
  ),
})

export const RequestUploadUrlOutputSchema = z.object({
  fileId: z.string().uuid(),
  storageKey: z.string(),
  upload: z.discriminatedUnion('kind', [SinglePresignedUrlSchema, MultipartPresignedUrlSchema]),
})
export type RequestUploadUrlOutput = z.infer<typeof RequestUploadUrlOutputSchema>

// ----- Upload complete -----

const SinglePartCompleteSchema = z.object({
  kind: z.literal('single'),
})

const MultipartCompleteSchema = z.object({
  kind: z.literal('multipart'),
  uploadId: z.string().min(1),
  parts: z
    .array(
      z.object({
        partNumber: z.number().int().positive(),
        etag: z.string().min(1),
      }),
    )
    .min(1),
})

export const CompleteUploadInputSchema = z.object({
  durationSec: z.number().int().nonnegative().nullable().optional(),
  upload: z.discriminatedUnion('kind', [SinglePartCompleteSchema, MultipartCompleteSchema]),
})
export type CompleteUploadInput = z.infer<typeof CompleteUploadInputSchema>

// ----- Rename -----

/**
 * ファイル名（表示用 originalName）の変更入力。
 * - 改行・タブ・先頭末尾空白は弾く（誤入力 / コピペ事故防止）
 * - 最大長は RequestUploadUrl と揃える
 */
export const RenameFileInputSchema = z.object({
  originalName: z
    .string()
    .min(1)
    .max(MAX_FILE_NAME_LEN)
    .refine((s) => s === s.trim(), '先頭・末尾の空白は使えません')
    .refine((s) => !/[\r\n\t]/.test(s), '改行・タブは使えません'),
})
export type RenameFileInput = z.infer<typeof RenameFileInputSchema>

// ----- Upload abort -----

/**
 * アップロード中断通知（クライアントが ✕ で止めた時に呼ぶ）。
 * uploadId はマルチパート時のみ送る。
 */
export const AbortUploadInputSchema = z.object({
  uploadId: z.string().min(1).optional(),
})
export type AbortUploadInput = z.infer<typeof AbortUploadInputSchema>

// ----- Download URL output -----

export const DownloadUrlOutputSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string().datetime(),
})
export type DownloadUrlOutput = z.infer<typeof DownloadUrlOutputSchema>

// ----- Stream playback -----

export const StreamTokenOutputSchema = z.object({
  token: z.string(),
  expiresAt: z.string().datetime(),
  hlsManifestUrl: z.string().url(),
  iframeUrl: z.string().url(),
})
export type StreamTokenOutput = z.infer<typeof StreamTokenOutputSchema>

export const PreviewThumbnailOutputSchema = z.object({
  url: z.string().url(),
})
export type PreviewThumbnailOutput = z.infer<typeof PreviewThumbnailOutputSchema>

export const PlaybackEventInputSchema = z.object({
  /**
   * 再生イベント種別。
   * - start:    再生開始（初回 playing 時 1 度だけ送る）
   * - progress: 再生位置の定期通知（30 秒間隔目安）
   * - seek:     シーク発生
   * - end:      動画末尾到達 / 視聴終了
   */
  event: z.enum(['start', 'progress', 'seek', 'end']),
  playPositionSec: z.number().int().nonnegative(),
  /** 当該セッションの累計視聴秒数（progress / end 時に送る） */
  playedSeconds: z.number().int().nonnegative().optional(),
})
export type PlaybackEventInput = z.infer<typeof PlaybackEventInputSchema>

// ----- List response (paginated) -----

export const ListFilesResultSchema = z.object({
  items: z.array(FileSchema),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
})
export type ListFilesResult = z.infer<typeof ListFilesResultSchema>
